import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Feather, Flame, ChevronRight, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Soft prompt + streak nudge that only renders for users with book_access.
// Shows today's words / target, current streak, sparkline of last 7 days,
// and a one-click button to open The Young CEO studio.
export default function DailyPagesWidget() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/book/stats');
      setStats(data);
    } catch (e) {
      // silent — widget hides if API fails
    }
  }, [api]);

  useEffect(() => {
    if (user?.book_access) load();
  }, [user, load]);

  if (!user?.book_access || !stats) return null;

  const pct = Math.min(stats.daily_pct || 0, 100);
  const hitGoal = (stats.today_words || 0) >= (stats.daily_target || 0);
  const remaining = Math.max((stats.daily_target || 0) - (stats.today_words || 0), 0);

  // Last 7 days of history for compact sparkline
  const last7 = (stats.history || []).slice(-7);
  const maxBar = Math.max(...last7.map(h => h.words), stats.daily_target || 1);

  return (
    <Card
      className="border-[#DAD0BA] shadow-sm overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #FAF6EC 0%, #F5EBD0 100%)',
        fontFamily: 'Crimson Pro, Georgia, serif',
      }}
      data-testid="daily-pages-widget"
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          {/* Left: prompt + progress */}
          <div className="flex-1 min-w-0">
            <div
              className="text-[10px] font-semibold tracking-widest uppercase text-[#8B5A2B] flex items-center gap-1.5 mb-1"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              <Feather className="h-3 w-3" /> The Young CEO · Daily Pages
            </div>

            <h3
              className="text-2xl font-semibold text-[#2A2118] leading-tight"
              data-testid="daily-pages-prompt"
            >
              {hitGoal
                ? `You hit today's pages. ${stats.today_words} words written.`
                : stats.today_words > 0
                  ? `${remaining} more words to today's goal.`
                  : `Write ${stats.daily_target} words today.`}
            </h3>

            {/* Progress bar */}
            <div className="mt-3 flex items-center gap-3">
              <div className="flex-1 h-2 rounded-full bg-[#ECE3CE] overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${pct}%`,
                    background: hitGoal
                      ? 'linear-gradient(90deg, #6B8E4E 0%, #C8A45A 100%)'
                      : 'linear-gradient(90deg, #8B5A2B 0%, #C8A45A 100%)',
                  }}
                  data-testid="daily-pages-progress"
                />
              </div>
              <div
                className="text-xs font-semibold text-[#5C4E3F]"
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
              >
                {stats.today_words} / {stats.daily_target}
              </div>
            </div>

            {/* Streak + sparkline row */}
            <div className="mt-3 flex items-center gap-4 flex-wrap">
              <div
                className="flex items-center gap-1.5 text-sm text-[#5C4E3F]"
                data-testid="daily-pages-streak"
              >
                <Flame className="h-4 w-4 text-[#B05B3B]" />
                <span className="font-semibold text-[#2A2118]">{stats.streak}-day streak</span>
              </div>

              <div className="flex items-end gap-1 h-6" title="Last 7 days">
                {last7.map((h, i) => {
                  const isToday = i === last7.length - 1;
                  const height = h.words === 0 ? 3 : Math.max(5, (h.words / maxBar) * 22);
                  return (
                    <div
                      key={h.date}
                      className="rounded-sm transition-all"
                      style={{
                        width: '5px',
                        height: `${height}px`,
                        background: isToday
                          ? '#C8A45A'
                          : h.words > 0
                            ? '#8B5A2B'
                            : '#DAD0BA',
                        boxShadow: isToday ? '0 0 4px rgba(200, 164, 90, 0.6)' : 'none',
                      }}
                    />
                  );
                })}
              </div>

              <span
                className="text-[11px] italic text-[#8A7C6A]"
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
              >
                {stats.total_words.toLocaleString()} of {stats.total_target.toLocaleString()} total
              </span>
            </div>
          </div>

          {/* Right: CTA */}
          <Button
            onClick={() => navigate('/book')}
            className="bg-[#2A2118] hover:bg-[#5E3E1F] text-[#FAF6EC] border border-[#2A2118] gap-1.5 px-5 h-11"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            data-testid="daily-pages-cta"
          >
            {hitGoal ? <Check className="h-4 w-4" /> : <Feather className="h-4 w-4" />}
            {hitGoal ? 'Keep writing' : 'Open studio'}
            <ChevronRight className="h-4 w-4 -ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
